import React, { useState } from 'react';
import styled from '@emotion/styled';
import { SerializedStyles } from '@emotion/react';
import { AigleInput, AigleInputProps } from './AigleInput';
import { aigleColor } from '@styles/colorStyleGuide';

/**
 * AiglePasswordInput Props
 */
export interface AiglePasswordInputProps extends Omit<AigleInputProps, 'type' | 'endAdornment'> {
  /** 비밀번호 표시 아이콘 (숨김) */
  visibilityOffIcon?: React.ReactNode;
  /** 비밀번호 표시 아이콘 (표시) */
  visibilityOnIcon?: React.ReactNode;
  /** 초기 비밀번호 표시 여부 */
  defaultVisible?: boolean;
  /** 비밀번호 표시/숨김 제어 (제어 컴포넌트로 사용 시) */
  visible?: boolean;
  /** 비밀번호 표시/숨김 변경 콜백 */
  onVisibilityChange?: (visible: boolean) => void;
}

// Styled Components
const VisibilityButton = styled.button`
  padding: 4px;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${aigleColor.grey.dark};
  transition: color 0.2s ease-in-out;
  
  &:hover {
    color: ${aigleColor.grey.darkest};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

// 기본 아이콘 (SVG로 직접 구현)
const DefaultVisibilityOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.83L19.56 16.75C21.07 15.49 22.26 13.86 22.99 12C21.26 7.61 16.99 4.5 11.99 4.5C10.59 4.5 9.25 4.75 8.01 5.2L10.17 7.36C10.74 7.13 11.35 7 12 7ZM2 4.27L4.28 6.55L4.74 7.01C3.08 8.3 1.78 10.02 1 12C2.73 16.39 7 19.5 12 19.5C13.55 19.5 15.03 19.2 16.38 18.66L16.8 19.08L19.73 22L21 20.73L3.27 3L2 4.27ZM7.53 9.8L9.08 11.35C9.03 11.56 9 11.78 9 12C9 13.66 10.34 15 12 15C12.22 15 12.44 14.97 12.65 14.92L14.2 16.47C13.53 16.8 12.79 17 12 17C9.24 17 7 14.76 7 12C7 11.21 7.2 10.47 7.53 9.8ZM11.84 9.02L14.99 12.17L15.01 12.01C15.01 10.35 13.67 9.01 12.01 9.01L11.84 9.02Z"
      fill="currentColor"
    />
  </svg>
);

const DefaultVisibilityOnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * AiglePasswordInput Component
 * 
 * 비밀번호 입력을 위한 Input 컴포넌트
 * 비밀번호 표시/숨김 토글 기능 포함
 * 
 * @example
 * // Basic Password Input
 * <AiglePasswordInput placeholder="비밀번호" />
 * 
 * @example
 * // Password Input with Label
 * <AiglePasswordInput 
 *   label="비밀번호" 
 *   placeholder="비밀번호를 입력하세요"
 * />
 * 
 * @example
 * // Floating Label (MUI Style)
 * <AiglePasswordInput 
 *   label="비밀번호"
 *   floatingLabel
 * />
 * 
 * @example
 * // Controlled Visibility
 * const [visible, setVisible] = useState(false);
 * <AiglePasswordInput 
 *   visible={visible}
 *   onVisibilityChange={setVisible}
 *   placeholder="비밀번호"
 * />
 */
export const AiglePasswordInput = React.forwardRef<HTMLInputElement, AiglePasswordInputProps>(
  (
    {
      visibilityOffIcon = <DefaultVisibilityOffIcon />,
      visibilityOnIcon = <DefaultVisibilityOnIcon />,
      defaultVisible = false,
      visible: controlledVisible,
      onVisibilityChange,
      ...props
    },
    ref
  ) => {
    const [internalVisible, setInternalVisible] = useState(defaultVisible);
    
    // 제어 컴포넌트 vs 비제어 컴포넌트 처리
    const isControlled = controlledVisible !== undefined;
    const isVisible = isControlled ? controlledVisible : internalVisible;
    
    const handleToggleVisibility = () => {
      const newVisible = !isVisible;
      
      if (isControlled) {
        onVisibilityChange?.(newVisible);
      } else {
        setInternalVisible(newVisible);
      }
    };
    
    const endAdornment = (
      <VisibilityButton
        type="button"
        onClick={handleToggleVisibility}
        onMouseDown={(e) => e.preventDefault()}
        aria-label={isVisible ? "비밀번호 숨기기" : "비밀번호 표시"}
      >
        {isVisible ? visibilityOnIcon : visibilityOffIcon}
      </VisibilityButton>
    );
    
    return (
      <AigleInput
        ref={ref}
        type={isVisible ? 'text' : 'password'}
        endAdornment={endAdornment}
        {...props}
      />
    );
  }
);

AiglePasswordInput.displayName = 'AiglePasswordInput';

export default AiglePasswordInput;

